import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUser, FiCalendar, FiAward, FiExternalLink, FiTrendingUp, FiUsers, FiGlobe, FiShield } = FiIcons;

const About = () => {
    const milestones = [
        {
            year: '2008',
            title: 'Company Founded',
            description: 'Softvative Inc was established with a vision to create innovative software solutions.'
        },
        {
            year: '2015',
            title: 'First Enterprise Client',
            description: 'Secured our first major enterprise contract, marking our entry into the B2B market.'
        },
        {
            year: '2020',
            title: 'Cloud Transformation',
            description: 'Transitioned to cloud-first architecture, enabling global scalability.'
        },
        {
            year: '2024',
            title: 'Expense Tracker Launch',
            description: 'Launched our flagship expense tracking SAAS platform with AI-powered insights.'
        }
    ];

    const values = [
        {
            icon: FiTrendingUp,
            title: 'Innovation',
            description: 'We constantly push the boundaries of what\'s possible with cutting-edge technology.'
        },
        {
            icon: FiUsers,
            title: 'Customer Focus',
            description: 'Our customers are at the heart of everything we do. Their success is our success.'
        },
        {
            icon: FiShield,
            title: 'Security',
            description: 'We maintain the highest standards of security and privacy for all user data.'
        },
        {
            icon: FiGlobe,
            title: 'Global Impact',
            description: 'We strive to make financial management accessible to everyone, everywhere.'
        }
    ];

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Hero Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center mb-16"
                >
                    <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                        About Softvative
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
                        Since 2008, we've been dedicated to creating innovative software solutions that
                        empower businesses and individuals to achieve their financial goals.
                    </p>
                </motion.div>

                {/* Leadership Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-16"
                >
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                        <div>
                            <div className="flex items-center mb-6">
                                <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center mr-6">
                                    <SafeIcon icon={FiUser} className="w-10 h-10 text-white" />
                                </div>
                            </div>

                            <div className="space-y-4 text-gray-600 dark:text-gray-300">
                                <p>
                                    With over 20 years of experience in software development and business leadership,
                                    Softvative was founded in 2008 with a vision to create technology that
                                    simplifies complex business processes.
                                </p>
                                <p>
                                    Under strong leadership, Softvative has grown from a small startup to a recognized
                                    leader in the SAAS industry, serving thousands of customers worldwide.
                                </p>
                            </div>

                            <div className="flex items-center mt-6 space-x-4">
                                <div className="flex items-center">
                                    <SafeIcon icon={FiCalendar} className="w-5 h-5 text-primary-500 mr-2" />
                                    <span className="text-sm text-gray-600 dark:text-gray-300">
                                        Founded in 2008
                                    </span>
                                </div>
                                <div className="flex items-center">
                                    <SafeIcon icon={FiAward} className="w-5 h-5 text-primary-500 mr-2" />
                                    <span className="text-sm text-gray-600 dark:text-gray-300">
                                        20+ Years Experience
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="relative">
                            <div className="bg-gradient-to-br from-primary-100 to-accent-100 dark:from-primary-900/20 dark:to-accent-900/20 rounded-2xl p-8 text-center">
                                <div className="w-32 h-32 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <span className="text-white font-bold text-4xl">S</span>
                                </div>
                                <blockquote className="text-gray-700 dark:text-gray-300 italic">
                                    "Our mission is to democratize financial management through
                                    innovative technology that's both powerful and easy to use."
                                </blockquote>
                            </div>
                        </div>
                    </div>
                </motion.div>

                {/* Company Timeline */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="mb-16"
                >
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-12">
                        Our Journey
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {milestones.map((milestone, index) => (
                            <motion.div
                                key={milestone.year}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg relative"
                            >
                                <div className="absolute -top-3 left-6 bg-gradient-to-r from-primary-500 to-accent-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                                    {milestone.year}
                                </div>
                                <div className="pt-4">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                        {milestone.title}
                                    </h3>
                                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                                        {milestone.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Values Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.6 }}
                    className="mb-16"
                >
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-12">
                        Our Values
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => (
                            <motion.div
                                key={value.title}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: 0.7 + index * 0.1 }}
                                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg text-center group hover:shadow-xl transition-all duration-300"
                            >
                                <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                                    <SafeIcon icon={value.icon} className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                                    {value.title}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-300 text-sm">
                                    {value.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Softvative Link Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.8 }}
                    className="bg-gradient-to-r from-primary-600 to-accent-600 rounded-2xl p-8 text-center text-white"
                >
                    <div className="flex justify-center mb-6">
                        <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                            <span className="text-white font-bold text-2xl">S</span>
                        </div>
                    </div>

                    <h2 className="text-2xl font-bold mb-4">
                        Learn More About Softvative
                    </h2>
                    <p className="text-primary-100 mb-6 max-w-2xl mx-auto">
                        Discover our full range of software solutions and services.
                        From custom development to enterprise solutions, we're here to help your business grow.
                    </p>

                    <a
                        href="https://www.softvative.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 bg-white text-primary-600 font-semibold rounded-xl hover:bg-gray-100 transition-colors duration-200 shadow-lg"
                    >
                        Visit Softvative.com
                        <SafeIcon icon={FiExternalLink} className="ml-2 w-4 h-4" />
                    </a>
                </motion.div>
            </div>
        </div>
    );
};

export default About;